<script lang="ts">
    import RewardsDynamicCustomerIcon from "@apps/rewards/components/RewardsDynamicCustomerIcon.svelte";
    import RewardsIcon from "@apps/rewards/components/RewardsIcon.svelte";
    import { REWARDS_BRANDING_ICONS } from "@apps/rewards/constants/constants";
    import type { RewardsStorefrontValuesBrandingEntity } from "@apps/rewards/modules/StorefrontValues/models/RewardsStorefrontValuesEntities";
    import type { BrandingLauncherPositionType } from "@apps/rewards/modules/StorefrontValues/types";
    import type { TranslationNamespaces } from "@apps/rewards/types";
    import CloseIcon from "@apps/rewards/widgets/rewards_popup_widget/components/icons/CloseIcon.svelte";
    import type { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import { afterUpdate, createEventDispatcher, onMount } from "svelte";
    import { fade } from "svelte/transition";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Text from "@components/Typography/Text.svelte";

    //Props
    export let isPopUpWidgetOpen: boolean;
    export let i18n: GwI18n;
    export let icon: RewardsStorefrontValuesBrandingEntity["popUpWidget"]["launcher"]["icon"];
    export let hideIcon: boolean;
    export let hideText: boolean;
    export let widgetPosition: BrandingLauncherPositionType;
    export let widgetNamespace: TranslationNamespaces;

    const dispatch = createEventDispatcher();

    //Variables
    let element: HTMLElement;
    let intialElementWidth: string;

    const launcherLabel$ = i18n.selectT(widgetNamespace, "launcher_label");

    const calculateButtonWidth = () => {
        if (!intialElementWidth) {
            intialElementWidth = element.clientWidth + "px";
        }
    };

    onMount(() => {
        const timeout = setTimeout(() => {
            dispatch("popupButtonLoad");
        }, 200);

        return () => {
            clearTimeout(timeout);
            dispatch("popupButtonRemove");
        };
    });

    $: $launcherLabel$ &&
        !hideText &&
        !isPopUpWidgetOpen &&
        afterUpdate(calculateButtonWidth);
</script>

<BaseButton
    style={`width: ${isPopUpWidgetOpen ? "60px" : intialElementWidth}`}
    ariaLabel={isPopUpWidgetOpen
        ? `Close ${$launcherLabel$} popup`
        : `Open ${$launcherLabel$} popup`}
    class="gw-rd-popup-widget-launcher-btn gw-rd-popup-widget-launcher-btn--{widgetPosition}"
    on:click
    on:mouseover
    bind:element
    withoutReset
>
    {#if !isPopUpWidgetOpen}
        <div
            class="gw-rd-popup-widget-launcher-btn__content"
            class:hide={!hideIcon}
            in:fade={{ duration: 100, delay: 100 }}
        >
            <div class="gw-rd-popup-widget-launcher-btn__content-icon">
                {#if !hideIcon}
                    <RewardsDynamicCustomerIcon
                        customerIcon={icon.customUrl}
                        defaultIcon={REWARDS_BRANDING_ICONS[icon.type]}
                    />
                {/if}
            </div>
            {#if $launcherLabel$ && !hideText}
                <Text component="span" variant="button" weight="semibold">
                    {$launcherLabel$}
                </Text>
            {/if}
        </div>
    {:else}
        <div
            class="gw-rd-popup-widget-launcher-btn__close-icon"
            in:fade={{ duration: 100, delay: 100 }}
        >
            <RewardsIcon isBrandingIconColor={false}>
                <CloseIcon />
            </RewardsIcon>
        </div>
    {/if}
</BaseButton>

<style lang="postcss">
    @define-mixin spacing $desktopRight, $desktopLeft, $mobileRight, $mobileLeft,
        $class {
        :global(
                .gw-rd-popup-widget-launcher-btn.gw-rd-popup-widget-launcher-btn--$(class)
            ) {
            position: fixed;
            top: auto;

            @media (width >= 768px) {
                right: $desktopRight;
                left: $desktopLeft;
                bottom: var(
                    --gw-rd-popup-widget-launcher__desktop_bottom-spacing
                );
            }

            @media (width < 768px) {
                width: auto !important;
                right: $mobileRight;
                left: $mobileLeft;
                bottom: var(
                    --gw-rd-popup-widget-launcher__mobile_bottom-spacing
                );

                :global(.hide .gw-layout-text) {
                    display: none;
                }
            }
        }
    }

    :global(.gw-rd-popup-widget-launcher-btn.gw-btn) {
        height: 60px;
        padding: var(--gw-rd-space-9);
        border-radius: 40px;
        background-color: var(--gw-rd-popup-widget-launcher__theme-color);
        border: none;
        transition: width 0.15s ease-in-out;
        text-wrap: nowrap;
        cursor: pointer;
    }

    @mixin spacing var(--gw-rd-popup-widget-launcher__desktop_side-spacing),
        auto, var(--gw-rd-popup-widget-launcher__mobile_side-spacing), auto, BR;
    @mixin spacing auto,
        var(--gw-rd-popup-widget-launcher__desktop_side-spacing), auto,
        var(--gw-rd-popup-widget-launcher__mobile_side-spacing), BL;

    .gw-rd-popup-widget-launcher-btn__content {
        display: flex;
        align-items: center;
        gap: var(--gw-rd-space-5);

        &-icon {
            width: 24px;
            height: 24px;

            &:empty {
                display: none;
            }
        }

        :global(.gw-layout-text) {
            color: var(--gw-rd-popup-widget-launcher__font-color);
        }
    }

    .gw-rd-popup-widget-launcher-btn__content,
    .gw-rd-popup-widget-launcher-btn__close-icon {
        color: var(--gw-rd-popup-widget-launcher__icon-color);
    }
</style>
